import { format } from 'date-fns';

export const getSubscriptionStatusDisplay = (subscription) => {
  if (!subscription) return 'Not available';

  if (subscription.displayStatus) {
    return subscription.displayStatus;
  }

  if (
    subscription.isCanceled &&
    !subscription.isCanceledWithGracePeriod &&
    !subscription.isCanceledDuringTrial
  ) {
    return 'Cancelled';
  }

  if (subscription.isCanceledDuringTrial) {
    const endDate = format(new Date(subscription.trialEndsAt), 'dd MMM yyyy');
    return `Trial cancelled (ends ${endDate})`;
  }

  if (subscription.isCanceledWithGracePeriod) {
    const endDate = format(new Date(subscription.endsAt), 'dd MMM yyyy');
    return `Cancelled (ends ${endDate})`;
  }

  if (subscription.isCanceled) {
    return 'Cancelled';
  }

  if (subscription.isOnTrial) {
    const endDate = format(new Date(subscription.trialEndsAt), 'dd MMM yyyy');
    return `Trial (until ${endDate})`;
  }

  switch (subscription.status) {
    case 'active':
      return 'Active';
    case 'past_due':
      return 'Past due';
    case 'unpaid':
      return 'Unpaid';
    case 'incomplete':
      return 'Incomplete';
    case 'incomplete_expired':
      return 'Payment failed';
    default:
      return subscription.status
        ? subscription.status.charAt(0).toUpperCase() +
            subscription.status.slice(1)
        : 'Unknown';
  }
};

export const getNextPaymentText = (subscription) => {
  if (!subscription) return 'No payment information available';

  if (
    subscription.isCanceled &&
    !subscription.isCanceledWithGracePeriod &&
    !subscription.isCanceledDuringTrial
  ) {
    return 'No upcoming payments';
  }

  if (subscription.isCanceledDuringTrial) {
    const endDate = format(new Date(subscription.trialEndsAt), 'dd MMM yyyy');
    return `Your trial will end on ${endDate}`;
  }

  if (subscription.isOnTrial) {
    const endDate = format(new Date(subscription.trialEndsAt), 'dd MMM yyyy');
    return `Your trial ends on ${endDate}`;
  }

  if (subscription.status === 'active' && subscription.endsAt) {
    const endDate = format(new Date(subscription.endsAt), 'dd MMM yyyy');
    return `Next payment on ${endDate}`;
  }

  return 'No upcoming payments';
};

// Mapping of plural feature names to their singular forms
const SINGULAR_NAMES = {
  Seats: 'Seat',
  Passes: 'Pass',
  'Pass Templates': 'Pass Template',
  'Pass Download Pages': 'Pass Download Page',
  'Custom Pass Issuance Forms': 'Custom Pass Issuance Form',
  Entities: 'Entity',
  'Reader Logins': 'Reader Login',
};

export const monthlyAmountMinorUnits = (price) => {
  if (!price || price.amount == null || price.amount === undefined) return null;
  const n = Number(price.amount);
  if (Number.isNaN(n)) return null;
  if (price.interval === 'year') return Math.round(n / 12);
  if (price.interval === 'month') return n;
  return n;
};

export const annualSavingsVsMonthlyPrice = (prices) => {
  if (!prices?.length) return null;
  const month = prices.find((p) => p.interval === 'month');
  const year = prices.find((p) => p.interval === 'year');
  if (
    month == null ||
    month.amount == null ||
    year == null ||
    year.amount == null
  ) {
    return null;
  }
  const monthlyMinor = Number(month.amount);
  const yearlyMinor = Number(year.amount);
  if (Number.isNaN(monthlyMinor) || Number.isNaN(yearlyMinor)) return null;
  const fullAnnualFromMonthly = monthlyMinor * 12;
  if (!(fullAnnualFromMonthly > yearlyMinor)) return null;
  const discountPercent = Math.round(
    ((fullAnnualFromMonthly - yearlyMinor) / fullAnnualFromMonthly) * 100
  );
  return {
    discountPercent,
    listMonthlyMinor: monthlyMinor,
  };
};

export const unlockedFirst = (items, isUnlocked) => {
  const unlocked = [];
  const locked = [];
  for (const item of items || []) {
    (isUnlocked(item) ? unlocked : locked).push(item);
  }
  return [...unlocked, ...locked];
};

export const columnMajorGridProps = (count, columnsByBreakpoint) => {
  const gridTemplateRows = {};
  for (const [breakpoint, columns] of Object.entries(columnsByBreakpoint)) {
    const rows = Math.max(1, Math.ceil((count || 0) / columns));
    gridTemplateRows[breakpoint] = `repeat(${rows}, auto)`;
  }
  return { gridAutoFlow: 'column', gridAutoColumns: '1fr', gridTemplateRows };
};

export const newColumnStartProps = (columnsByBreakpoint) => {
  const gridRowStart = {};
  for (const [breakpoint, columns] of Object.entries(columnsByBreakpoint)) {
    gridRowStart[breakpoint] = columns > 1 ? 1 : 'auto';
  }
  return { gridRowStart };
};

export const annualOptionSavingPercent = (annualOption) => {
  if (!annualOption) return null;
  const monthly = Number(annualOption.monthlyAmountPence);
  const saving = Number(annualOption.annualSavingPence);
  if (Number.isNaN(monthly) || Number.isNaN(saving) || monthly <= 0)
    return null;
  const fullAnnualFromMonthly = monthly * 12;
  if (fullAnnualFromMonthly <= 0) return null;
  return Math.round((saving / fullAnnualFromMonthly) * 100);
};

export const maxAnnualDiscountPercentAcrossPlans = (plans) => {
  let max = 0;
  for (const plan of plans || []) {
    const s = annualSavingsVsMonthlyPrice(plan.prices);
    if (s && s.discountPercent > max) max = s.discountPercent;
  }
  return max;
};

export const formatGbpFromMinorUnits = (minorUnits, options = {}) => {
  const { wholePounds = false } = options;
  if (minorUnits == null || minorUnits === undefined) return '';
  const n = Number(minorUnits);
  if (Number.isNaN(n)) return '';
  const pounds = wholePounds ? Math.round(n / 100) : n / 100;
  return pounds.toLocaleString('en-GB', {
    style: 'currency',
    currency: 'GBP',
    ...(wholePounds
      ? { maximumFractionDigits: 0, minimumFractionDigits: 0 }
      : {}),
  });
};

export const formatCustomPlanName = (name) => {
  if (!name || typeof name !== 'string') return name ?? '';
  const parenIndex = name.indexOf('(');
  const cleaned = (parenIndex === -1 ? name : name.slice(0, parenIndex)).trim();
  return cleaned || name.trim();
};

export const monthlyEquivalentFromCustomOffer = (
  amountMinor,
  billingInterval
) => {
  if (amountMinor == null || amountMinor === undefined) return null;
  const n = Number(amountMinor);
  if (Number.isNaN(n)) return null;
  switch (billingInterval) {
    case 'annually':
      return Math.round(n / 12);
    case 'quarterly':
      return Math.round(n / 3);
    case 'semiannually':
      return Math.round(n / 6);
    case 'monthly':
    default:
      return n;
  }
};

export const billingFrequencyAdverb = (interval, intervalCount = 1) => {
  if (interval === 'year' && intervalCount === 1) return 'yearly';
  if (interval === 'month' && intervalCount === 3) return 'quarterly';
  return 'monthly';
};

export const billingPeriodNoun = (interval, intervalCount = 1) => {
  if (interval === 'year' && intervalCount === 1) return 'year';
  if (interval === 'month' && intervalCount === 3) return 'quarter';
  return 'month';
};

export const customOfferBilledIntervalPhrase = (billingInterval) => {
  switch (billingInterval) {
    case 'annually':
      return 'annually';
    case 'quarterly':
      return 'every 3 months';
    case 'semiannually':
      return 'semi-annually';
    case 'monthly':
    default:
      return 'monthly';
  }
};

export const formatFeature = (feature) => {
  if (!feature) return '';

  // For count-based features (seats, passes, templates, etc.)
  if (feature.limit !== null && feature.limit !== undefined) {
    const name =
      feature.limit === 1 && SINGULAR_NAMES[feature.name]
        ? SINGULAR_NAMES[feature.name]
        : feature.name;
    return `${feature.limit.toLocaleString()} ${name}`;
  }

  // For enforcement features like watermark
  if (feature.key === 'passentry_watermark') {
    return 'Watermark removal';
  }

  // For access-based features
  return feature.name;
};

export const getSubscriptionStatusAlert = (subscription) => {
  if (!subscription) return null;

  if (
    subscription.isCanceled &&
    !subscription.isCanceledWithGracePeriod &&
    !subscription.isCanceledDuringTrial
  ) {
    return {
      status: 'error',
      message: 'Your subscription has been cancelled.',
    };
  }

  if (subscription.isCanceledDuringTrial) {
    return {
      status: 'warning',
      message: `Your trial access will end on ${format(new Date(subscription.trialEndsAt), 'dd MMM yyyy')}`,
    };
  }

  if (subscription.isCanceledWithGracePeriod) {
    return {
      status: 'warning',
      message: `Your subscription has been cancelled and will end on ${format(new Date(subscription.endsAt), 'dd MMM yyyy')}. You'll have access until this date.`,
    };
  }

  if (subscription.status === 'past_due' || subscription.status === 'unpaid') {
    return {
      status: 'error',
      message: 'Your payment is past due. Please update your payment method.',
    };
  }

  if (
    subscription.status === 'incomplete' ||
    subscription.status === 'incomplete_expired'
  ) {
    return {
      status: 'error',
      message:
        "Your payment couldn't be processed. Please update your payment method.",
    };
  }

  return null;
};
